import { useEffect, useState } from "react";
import type { inferRouterOutputs } from "@trpc/server";
import { keepPreviousData } from "@tanstack/react-query";
import { format } from "date-fns";
import type { AppRouter } from "../../server/routers";
import { trpc } from "@/lib/trpc";
import UpdateBanner from "@/components/UpdateBanner";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertTriangle,
  ArrowLeft,
  CalendarClock,
  CalendarDays,
  ExternalLink,
  Loader2,
  MapPin,
  Pencil,
  Printer,
  RefreshCw,
  Sparkles,
  Stethoscope,
  Users,
} from "lucide-react";

type RecentsOutput = inferRouterOutputs<AppRouter>["footage"]["recentPatients"];
type RecentPatient = RecentsOutput["patients"][number];

const WEEKS_PRIOR = 2;

const LOCATIONS = ["Iowa", "Minnesota", "Colorado"] as const;
type LocationValue = (typeof LOCATIONS)[number] | "all";

// Region colors — same palette as the Location chips elsewhere in the app
// (regionBadge in FootageSearch): Iowa blue, Minnesota purple, Colorado green.
const REGION_COLORS: Record<(typeof LOCATIONS)[number], string> = {
  Iowa: "#5B7C99",
  Minnesota: "#6A5ACD",
  Colorado: "#1b6e4a",
};

/** Section header labels by index (current week first). */
function weekLabel(index: number): string {
  if (index === 0) return "This week";
  if (index === 1) return "Last week";
  if (index === 2) return "Two weeks ago";
  return `${index} weeks ago`;
}

/** Format an ISO timestamp as MM.dd.yy for the note attribution line. */
function shortDate(iso: string): string {
  try {
    return format(new Date(iso), "MM.dd.yy");
  } catch {
    return "";
  }
}

/**
 * Drive-style permission chip — mirrors permissionBadge() in FootageSearch so
 * the posting-status vocabulary reads the same across the app.
 */
function permissionBadge(permission: string | null): {
  label: string;
  dotClass: string | null;
  textClass: string;
} {
  switch (permission) {
    case "pre-approved":
      return {
        label: "Pre-Approved",
        dotClass: "bg-emerald-500",
        textClass: "text-emerald-700 dark:text-emerald-400",
      };
    case "check-before-posting":
      return {
        label: "Check Before Posting",
        dotClass: "bg-[#4986e7]",
        textClass: "text-[#4986e7]",
      };
    case "private-internal":
      return {
        label: "Private + Internal Use Only",
        dotClass: "bg-red-500",
        textClass: "text-red-600 dark:text-red-400",
      };
    default:
      return {
        label: "Unclassified",
        dotClass: null,
        textClass: "text-muted-foreground",
      };
  }
}

function StatusBadge({
  permission,
  hasSpecialInstructions,
}: {
  permission: string | null;
  hasSpecialInstructions: boolean;
}) {
  const badge = permissionBadge(permission);
  return (
    <div className="flex flex-col items-start gap-1.5">
      <span
        className={cn(
          "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs font-medium",
          badge.textClass,
        )}
      >
        {badge.dotClass && (
          <span className={cn("h-2 w-2 flex-shrink-0 rounded-full", badge.dotClass)} />
        )}
        {badge.label}
      </span>
      {hasSpecialInstructions && (
        <span className="inline-flex items-center whitespace-nowrap rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-400">
          + Special Instructions
        </span>
      )}
    </div>
  );
}

/** Shared, editable note for one patient — visible to all users. */
function NotesCell({ p }: { p: RecentPatient }) {
  const [note, setNote] = useState<string | null>(p.note);
  const [updatedBy, setUpdatedBy] = useState<string | null>(p.noteUpdatedBy);
  const [updatedAt, setUpdatedAt] = useState<string | null>(p.noteUpdatedAt);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(p.note ?? "");
  const mutation = trpc.footage.setPatientNote.useMutation();

  // Adopt refetched note values (e.g. another user edited it) while we're not
  // actively editing, so a note added elsewhere shows up here too.
  useEffect(() => {
    if (!editing) {
      setNote(p.note);
      setUpdatedBy(p.noteUpdatedBy);
      setUpdatedAt(p.noteUpdatedAt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.note, p.noteUpdatedBy, p.noteUpdatedAt]);

  const save = async () => {
    try {
      const res = await mutation.mutateAsync({
        patientKey: p.patientKey,
        note: draft,
      });
      setNote(res.note);
      setUpdatedBy(res.noteUpdatedBy);
      setUpdatedAt(res.noteUpdatedAt);
      setEditing(false);
    } catch {
      /* keep the editor open so the text isn't lost */
    }
  };

  if (editing) {
    return (
      <div className="space-y-1.5">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          autoFocus
          className="w-full min-w-[13rem] rounded border border-border bg-background p-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder="Add a note the team can see…"
        />
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => void save()} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Save"}
          </Button>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => {
              setEditing(false);
              setDraft(note ?? "");
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return note ? (
    <div className="space-y-1">
      <p className="whitespace-pre-wrap text-sm text-foreground">{note}</p>
      <div className="text-xs text-muted-foreground">
        — {updatedBy ?? "someone"}
        {updatedAt ? ` · ${shortDate(updatedAt)}` : ""}
      </div>
      <button
        type="button"
        onClick={() => {
          setDraft(note);
          setEditing(true);
        }}
        className="print-hidden inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <Pencil className="h-3 w-3" />
        Edit
      </button>
    </div>
  ) : (
    <Button
      size="sm"
      variant="outline"
      className="print-hidden gap-1.5"
      onClick={() => {
        setDraft("");
        setEditing(true);
      }}
    >
      <Pencil className="h-3.5 w-3.5" />
      Add note
    </Button>
  );
}

/** One patient row — owns its own AI-overview fetch/state. */
function PatientRow({
  p,
  showLocation,
}: {
  p: RecentPatient;
  /** Region line is only useful in All Locations view; hidden otherwise. */
  showLocation: boolean;
}) {
  // Seed from the shared cached overview the server returns inline, so an
  // overview generated by any user is visible to everyone with no click and
  // no re-generation. Local generation updates it in place; it persists for
  // the life of the page (including when the patient shifts week sections).
  const [overview, setOverview] = useState<string | null>(p.overview);
  // Whether the cached overview predates the newest footage for this patient.
  const [stale, setStale] = useState(p.overviewStale);
  const mutation = trpc.footage.patientOverview.useMutation();

  // Adopt a freshly-shared overview that arrives via refetch (e.g. another
  // user generated it) without overwriting one we generated locally.
  useEffect(() => {
    if (p.overview !== null) setOverview((cur) => cur ?? p.overview);
  }, [p.overview]);
  useEffect(() => {
    setStale(p.overviewStale);
  }, [p.overviewStale]);

  const loadOverview = async () => {
    try {
      const res = await mutation.mutateAsync({
        patientKey: p.patientKey,
        weeksPrior: WEEKS_PRIOR,
      });
      setOverview(res.overview);
      setStale(false); // freshly regenerated — no longer behind the footage
    } catch (err) {
      setOverview(
        err instanceof Error
          ? `Couldn't generate overview: ${err.message}`
          : "Couldn't generate overview.",
      );
    }
  };

  return (
    <tr className="border-t border-border align-top">
      {/* Patient — name, location, and posting status stacked to save a column */}
      <td className="px-3 py-3">
        <div className="font-medium text-foreground">
          {p.subjectName ?? "(unnamed)"}
        </div>
        {showLocation && p.region && (
          <div className="mt-0.5 inline-flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3" />
            {p.region}
          </div>
        )}
        <div className="mt-1.5">
          <StatusBadge
            permission={p.permission}
            hasSpecialInstructions={p.hasSpecialInstructions}
          />
        </div>
      </td>

      {/* Filmed by */}
      <td className="px-3 py-3">
        {p.filmedBy.length > 0 ? (
          <div className="flex flex-col items-start gap-1">
            {p.filmedBy.map((name) => (
              <span
                key={name}
                className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-foreground"
              >
                <Users className="h-3 w-3 text-muted-foreground" />
                {name}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>

      {/* Clips */}
      <td className="px-3 py-3 text-center font-medium text-foreground">
        {p.totalClips}
      </td>

      {/* Days filmed — one hyperlinked chip per dated folder, stacked; the
          exam folder leads the list and carries the stethoscope */}
      <td className="px-3 py-3">
        {p.dayLinks.length > 0 ? (
          <div className="flex flex-col items-start gap-1">
            {p.dayLinks.map((d, i) => (
              <a
                key={`${d.label}-${i}`}
                href={d.url}
                target="_blank"
                rel="noreferrer"
                className={cn(
                  "inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs hover:underline",
                  d.isExam
                    ? "border-emerald-300 bg-emerald-50 font-medium text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-400"
                    : "border-border bg-muted/40 text-primary",
                )}
              >
                {d.isExam && <Stethoscope className="h-3 w-3" />}
                {d.label}
                <ExternalLink className="h-2.5 w-2.5" />
              </a>
            ))}
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">
            {p.distinctDays} day{p.distinctDays === 1 ? "" : "s"}
          </span>
        )}
      </td>

      {/* AI overview */}
      <td className="px-3 py-3 min-w-[16rem] max-w-sm">
        {mutation.isPending ? (
          <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Generating…
          </div>
        ) : overview !== null ? (
          <div className="space-y-1">
            <p className="whitespace-pre-wrap text-sm text-foreground">
              {overview}
            </p>
            {stale && (
              <div className="flex items-start gap-1 text-xs font-medium text-amber-600 dark:text-amber-400">
                <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                New footage since this overview
              </div>
            )}
            <button
              type="button"
              onClick={() => void loadOverview()}
              className="print-hidden inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <RefreshCw className="h-3 w-3" />
              Regenerate
            </button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="print-hidden gap-1.5"
            onClick={() => void loadOverview()}
          >
            <Sparkles className="h-3.5 w-3.5" />
            Generate overview
          </Button>
        )}
      </td>

      {/* Notes — shared, editable, visible to all users */}
      <td className="px-3 py-3 min-w-[14rem] max-w-sm">
        <NotesCell p={p} />
      </td>
    </tr>
  );
}

export default function Recents() {
  const { user } = useAuth({ redirectOnUnauthenticated: true });
  const [includePrivate, setIncludePrivate] = useState(false);

  // Location toggle — defaults to the signed-in user's designated location
  // (falling back to "All Locations" when they have none), matching how the
  // rest of the app pre-selects a location. `null` until the user loads so
  // the first query doesn't fire against the wrong location.
  const [location, setLocation] = useState<LocationValue | null>(null);
  useEffect(() => {
    if (location === null && user) {
      setLocation((user.defaultLocation as LocationValue | null) ?? "all");
    }
  }, [user, location]);

  // "View previous weeks" — an ISO yyyy-MM-dd date; when set, the query shows
  // only the Mon–Sun week containing it. null = the default rolling window.
  const [historyWeek, setHistoryWeek] = useState<string | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const inHistory = historyWeek !== null;

  // Single-section print target (a weekStart); while set, other sections are
  // hidden from print and window.print() is fired, then reset on afterprint.
  const [printOnlyWeek, setPrintOnlyWeek] = useState<string | null>(null);
  useEffect(() => {
    if (printOnlyWeek === null) return;
    const reset = () => setPrintOnlyWeek(null);
    window.addEventListener("afterprint", reset, { once: true });
    const id = window.requestAnimationFrame(() => window.print());
    return () => {
      window.cancelAnimationFrame(id);
      window.removeEventListener("afterprint", reset);
    };
  }, [printOnlyWeek]);

  const query = trpc.footage.recentPatients.useQuery(
    {
      weeksPrior: WEEKS_PRIOR,
      includePrivateInternal: includePrivate,
      region: location && location !== "all" ? location : undefined,
      weekOf: historyWeek ?? undefined,
    },
    { placeholderData: keepPreviousData, enabled: location !== null },
  );

  const data = query.data;
  const patients = data?.patients ?? [];
  const weekStarts = data?.weekStarts ?? [];

  const locationLabel = location === "all" || !location ? "All Locations" : location;
  const scopeLabel = inHistory
    ? `Week of ${weekStarts[0] ?? historyWeek}`
    : "This week + prior two weeks";

  return (
    <div className="min-h-screen bg-background">
      <UpdateBanner />
      <AppHeader />
      <main className="container py-8">
        <div className="mx-auto max-w-6xl space-y-6">
          {/* Header */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <CalendarClock className="h-5 w-5 text-primary" />
                  <h1 className="text-xl font-semibold text-foreground">Recents</h1>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Patients filmed this week and the two weeks prior.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 print-hidden">
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox
                    checked={includePrivate}
                    onCheckedChange={(v) => setIncludePrivate(v === true)}
                  />
                  Include Private + Internal patients
                </label>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={() => window.print()}
                >
                  <Printer className="h-3.5 w-3.5" />
                  Print all
                </Button>
              </div>
            </div>

            {/* Location toggle — one per location + All Locations */}
            <div className="inline-flex flex-wrap items-center gap-1 rounded-lg border border-border bg-muted/40 p-1 print-hidden">
              {(["all", ...LOCATIONS] as const).map((loc) => {
                const active = location === loc;
                const color = loc !== "all" ? REGION_COLORS[loc] : undefined;
                return (
                  <button
                    key={loc}
                    type="button"
                    onClick={() => setLocation(loc)}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                      active && "shadow-sm",
                      active && !color && "bg-foreground text-background",
                      !active && "text-muted-foreground hover:text-foreground",
                    )}
                    style={
                      active && color
                        ? { backgroundColor: color, color: "#ffffff" }
                        : undefined
                    }
                  >
                    {color ? (
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: active ? "#ffffff" : color }}
                      />
                    ) : (
                      <MapPin className="h-3.5 w-3.5" />
                    )}
                    {loc === "all" ? "All Locations" : loc}
                  </button>
                );
              })}
            </div>

            {/* Printed-only context line */}
            <div className="print-only text-sm text-foreground">
              Recents — {locationLabel} — {scopeLabel} — printed{" "}
              {format(new Date(), "MM.dd.yy")}
            </div>

            {/* History-mode banner + back button */}
            {inHistory && (
              <div className="flex items-center gap-3 print-hidden">
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1.5"
                  onClick={() => setHistoryWeek(null)}
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back to recent weeks
                </Button>
                <span className="text-sm text-muted-foreground">
                  Viewing {scopeLabel}
                </span>
              </div>
            )}
          </div>

          {/* States */}
          {location === null || query.isLoading ? (
            <Card>
              <CardContent className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                Loading recent patients…
              </CardContent>
            </Card>
          ) : query.isError ? (
            <Card>
              <CardContent className="py-12 text-center text-sm text-red-600 dark:text-red-400">
                Couldn't load recent patients: {query.error.message}
              </CardContent>
            </Card>
          ) : patients.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                {inHistory
                  ? "No patients filmed that week."
                  : "No patients filmed in this window."}
              </CardContent>
            </Card>
          ) : (
            weekStarts.map((weekStart, i) => {
              const inWeek = patients.filter((p) => p.weekStart === weekStart);
              if (inWeek.length === 0) return null;
              const hiddenForPrint =
                printOnlyWeek !== null && printOnlyWeek !== weekStart;
              return (
                <section
                  key={weekStart}
                  className={cn("space-y-2", hiddenForPrint && "print-hidden")}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-baseline gap-2">
                      <h2 className="text-sm font-semibold uppercase tracking-wide text-foreground">
                        {inHistory ? `Week of ${weekStart}` : weekLabel(i)}
                      </h2>
                      <span className="text-xs text-muted-foreground">
                        {inHistory ? "" : `week of ${weekStart} · `}
                        {inWeek.length} patient{inWeek.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="gap-1.5 print-hidden"
                      onClick={() => setPrintOnlyWeek(weekStart)}
                    >
                      <Printer className="h-3.5 w-3.5" />
                      Print Section
                    </Button>
                  </div>
                  <Card className="overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                          <tr>
                            <th className="px-3 py-2 font-medium">Patient</th>
                            <th className="px-3 py-2 font-medium">Filmed By</th>
                            <th className="px-3 py-2 text-center font-medium">Clips</th>
                            <th className="px-3 py-2 font-medium">Days Filmed</th>
                            <th className="px-3 py-2 font-medium">AI Overview</th>
                            <th className="px-3 py-2 font-medium">Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inWeek.map((p) => (
                            <PatientRow
                              key={p.patientKey}
                              p={p}
                              showLocation={location === "all"}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </section>
              );
            })
          )}

          {/* View previous weeks — calendar picker */}
          {!inHistory && (
            <div className="flex justify-center pt-2 print-hidden">
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="gap-1.5">
                    <CalendarDays className="h-4 w-4" />
                    View previous weeks
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="center">
                  <Calendar
                    mode="single"
                    disabled={{ after: new Date() }}
                    onSelect={(d) => {
                      if (!d) return;
                      setHistoryWeek(format(d, "yyyy-MM-dd"));
                      setCalendarOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
